import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

import { DashboardShell } from "@/components/DashboardShell";
import { CUSTOMER_NAV } from "@/lib/nav";
import { StarRating } from "@/components/StarRating";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/lib/auth";
import {
  REVIEW_SORTS,
  REVIEW_SORT_LABEL,
  deleteReview,
  listCustomerReviews,
  sortReviews,
  summarize,
  type ReviewSort,
} from "@/lib/reviews";

export const Route = createFileRoute("/_authenticated/customer/reviews")({
  head: () => ({
    meta: [
      { title: "My Reviews · Landoo" },
      {
        name: "description",
        content: "Manage the ratings and reviews you have written for Landoo listings.",
      },
      { property: "og:title", content: "My Reviews · Landoo" },
      {
        property: "og:description",
        content: "Manage the ratings and reviews you have written for Landoo listings.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: CustomerReviewsPage,
});

function CustomerReviewsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [sort, setSort] = useState<ReviewSort>("latest");

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["customer", "reviews", user?.id],
    queryFn: () => listCustomerReviews(user!.id),
    enabled: !!user,
  });

  const summary = useMemo(() => summarize(rows), [rows]);
  const list = useMemo(() => sortReviews(rows, sort), [rows, sort]);

  const remove = useMutation({
    mutationFn: (id: string) => deleteReview(id),
    onSuccess: () => {
      toast.success("Review deleted.");
      qc.invalidateQueries({ queryKey: ["customer", "reviews", user?.id] });
    },
    onError: () => toast.error("Could not delete the review."),
  });

  return (
    <DashboardShell title="My Reviews" role="Customer" items={CUSTOMER_NAV}>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs text-muted-foreground">Average Rating you gave</p>
          <p className="mt-1 text-2xl font-bold text-brown-dark">
            {summary.average ? `${summary.average} / 5` : "—"}
          </p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs text-muted-foreground">Total Reviews</p>
          <p className="mt-1 text-2xl font-bold text-brown-dark">{summary.total}</p>
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Select value={sort} onValueChange={(v) => setSort(v as ReviewSort)}>
          <SelectTrigger className="w-full sm:w-48" aria-label="Sort reviews">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {REVIEW_SORTS.map((s) => (
              <SelectItem key={s} value={s}>
                {REVIEW_SORT_LABEL[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <p className="mt-6 text-sm text-muted-foreground">Loading your reviews…</p>
      ) : list.length === 0 ? (
        <p className="mt-6 rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          You have not written any reviews yet. Send an enquiry on a listing first, then rate it.
        </p>
      ) : (
        <ul className="mt-4 space-y-3">
          {list.map((r) => (
            <li key={r.id} className="rounded-xl border border-border bg-card p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <StarRating value={r.rating} size="sm" />
                  <Link
                    to="/property/$id"
                    params={{ id: r.listing_id }}
                    className="mt-1 block text-sm font-semibold text-brown-dark hover:underline"
                  >
                    {r.listing_title ?? "View listing"}
                  </Link>
                  <p className="text-xs text-muted-foreground">
                    {new Date(r.created_at).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                    {r.is_hidden ? " · Hidden by admin" : ""}
                  </p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => remove.mutate(r.id)}>
                  <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
                </Button>
              </div>
              {r.title && <p className="mt-2 text-sm font-medium text-brown-dark">{r.title}</p>}
              {r.body && (
                <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">{r.body}</p>
              )}
              {r.partner_reply && (
                <div className="mt-3 rounded-lg bg-secondary/50 p-2.5">
                  <p className="text-xs font-semibold text-brown-dark">Partner reply</p>
                  <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">
                    {r.partner_reply}
                  </p>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </DashboardShell>
  );
}
